import React, { useState, useEffect } from 'react';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';


/**
 * InboxAnalyticsCard - Analytics dashboard for Communications Hub
 * Shows key metrics for inbox and notifications
 */
const InboxAnalyticsCard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d'); // '7d', '30d', 'all'

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Calculate date range
      const now = new Date();
      let dateFrom = null;
      
      if (dateRange === '7d') {
        dateFrom = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      } else if (dateRange === '30d') {
        dateFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      }
      
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      
      const response = await fetch(
        `${BACKEND_URL}/api/notifications/analytics?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to load analytics');
      
      const data = await response.json();
      setAnalytics(data.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const now = new Date();
      let dateFrom = null;
      
      if (dateRange === '7d') {
        dateFrom = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      } else if (dateRange === '30d') {
        dateFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      }
      
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      
      const response = await fetch(
        `${BACKEND_URL}/api/notifications/export/csv?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to export');
      
      // Download the CSV
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `notifications_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export CSV:', error);
      alert('Failed to export data');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-200">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">📊 Inbox Analytics</h3>
          <p className="text-sm text-gray-600 mt-1">Communication metrics and insights</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Date Range Selector */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </select>
          
          {/* Export Button */}
          <button
            onClick={handleExportCSV}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
            title="Export to CSV"
          >
            📥 Export
          </button>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
          <div className="text-sm text-blue-700 mb-1">Total Messages</div>
          <div className="text-3xl font-bold text-blue-900">{analytics.total_messages}</div>
        </div>
        
        <div className="bg-green-50 p-4 rounded-xl border border-green-200">
          <div className="text-sm text-green-700 mb-1">Read Rate</div>
          <div className="text-3xl font-bold text-green-900">{analytics.read_rate_percent}%</div>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
          <div className="text-sm text-purple-700 mb-1">Total Reads</div>
          <div className="text-3xl font-bold text-purple-900">{analytics.total_reads}</div>
        </div>
        
        <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
          <div className="text-sm text-orange-700 mb-1">Avg Response Time</div>
          <div className="text-3xl font-bold text-orange-900">
            {analytics.median_approval_response_time_hours.toFixed(1)}h
          </div>
        </div>
      </div>

      {/* Top Unread Notifications */}
      {analytics.top_unread && analytics.top_unread.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Top Unread Notifications</h4>
          <div className="space-y-2">
            {analytics.top_unread.map((notif, idx) => (
              <div
                key={`item-${idx}`}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {notif.title}
                  </div>
                  <div className="text-xs text-gray-600">
                    {notif.module_type}
                  </div>
                </div>
                <div className="ml-4">
                  <span className="px-3 py-1 bg-orange-600 text-white text-xs rounded-full font-medium">
                    {notif.unread_count} unread
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <span className="text-2xl">💡</span>
          <div>
            <div className="text-sm font-semibold text-blue-900 mb-1">Insight</div>
            <p className="text-sm text-blue-800">
              {analytics.read_rate_percent >= 70 ? (
                `Great engagement! Your read rate of ${analytics.read_rate_percent}% is above the 70% benchmark.`
              ) : analytics.read_rate_percent >= 50 ? (
                `Good engagement at ${analytics.read_rate_percent}%. Consider using reminders for unread messages to reach 70%+.`
              ) : (
                `Read rate is ${analytics.read_rate_percent}%. Try sending reminders, checking message timing, or improving subject lines.`
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InboxAnalyticsCard;
